import { Add, LightbulbOutlined, NotificationsNone } from "@mui/icons-material";
import { genUid } from "components/playbook/CustomForm/common";
import ExampleItem from "components/todolist/Reminders/ExampleItem";
import ReminderItem from "components/todolist/Reminders/ReminderItem";
import { ExampleItemType, ReminderType } from "components/todolist/Reminders/typing";
import { FC, useEffect, useMemo, useState } from "react";
import { Control } from "react-hook-form";

interface RemindersProps {
  reminderList: ReminderType[];
  dispatch: React.Dispatch<any>;
  control: Control;
}

const MAX_ITEMS = 5;

const EXAMPLES: ExampleItemType[] = [
  {
    title: "Daily standup prep",
    description: "Remind me at 9 AM on the due date",
    payload: {
      id: genUid("reminder"),
      interval: 0,
      intervalBy: "day",
      reminderTime: "09:00:00",
      template: "Daily standup prep",
    },
  },
  {
    title: "Morning Briefing",
    description: "Remind me at 9 AM 1 days before the due date.",
    payload: {
      id: genUid("reminder"),
      interval: 1,
      intervalBy: "day",
      reminderTime: "09:00:00",
      template: "Morning Briefing",
    },
  },
  {
    title: "Important deadline",
    description: "Remind me at 9 AM 3 days before the due date.",
    payload: {
      id: genUid("reminder"),
      interval: 3,
      intervalBy: "day",
      reminderTime: "09:00:00",
      template: "Important deadline",
    },
  },
];

const Reminders: FC<RemindersProps> = ({ control, dispatch, reminderList }) => {
  const [showExample, setShowExample] = useState<boolean>(false);
  const [scroll, setScroll] = useState<boolean>(false);

  const disableAdd = useMemo(() => {
    return reminderList.length >= MAX_ITEMS;
  }, [reminderList]);

  const handleAdd = () => {
    dispatch({
      type: "add",
      payload: {
        id: genUid("reminder"),
        interval: 0,
        intervalBy: "day",
        reminderTime: "09:00:00",
        template: "Advance reminder",
      },
    });
  };

  const handleRemove = (id: string) => {
    dispatch({ type: "remove", payload: id });
  };

  const handleUpdate = (item: ReminderType) => {
    dispatch({ type: "update", payload: item });
  };

  const handleShowExample = () => {
    setShowExample(!showExample);
  };

  const handleAddExample = (item: ReminderType) => {
    dispatch({ type: "add", payload: item });
    setScroll(true);
  };

  useEffect(() => {
    if (reminderList.length > 0 && scroll) {
      const id = reminderList[reminderList.length - 1].id;
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
      setScroll(false);
    }
  }, [reminderList, scroll]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <NotificationsNone />
          <span style={{ fontWeight: "bold" }}>Reminders</span>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            cursor: "pointer",
            backgroundColor: showExample ? "#f5f5f5" : undefined,
            padding: "8px",
            borderRadius: "8px",
            userSelect: "none",
          }}
          onClick={handleShowExample}
        >
          <LightbulbOutlined />
          <span style={{ fontWeight: "bold" }}>{showExample ? "Hide" : "Examples"}</span>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        {showExample && (
          <div
            style={{
              backgroundColor: "#EFF6FF",
              border: "1px solid #D8E9FF",
              padding: "8px 16px",
              borderRadius: "8px",
            }}
          >
            <div style={{ fontWeight: "bold" }}>Quick setup:</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              {EXAMPLES.map((example) => (
                <ExampleItem
                  item={example}
                  key={example.title}
                  disabled={disableAdd}
                  onClick={() => handleAddExample(example.payload)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        {reminderList.length > 0
          ? reminderList.map((reminder) => (
              <ReminderItem
                key={reminder.id}
                item={reminder}
                onRemove={() => handleRemove(reminder.id)}
                onUpdate={handleUpdate}
                control={control}
              />
            ))
          : null}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            cursor: disableAdd ? "not-allowed" : "pointer",
            backgroundColor: disableAdd ? "#f5f5f5" : undefined,
            border: "1px solid #ddd",
            padding: "8px",
            borderRadius: "8px",
            userSelect: "none",
          }}
          onClick={disableAdd ? undefined : handleAdd}
        >
          <Add />
          <span style={{ fontWeight: "bold" }}>Add advance reminder</span>
        </div>
      </div>
    </div>
  );
};

export default Reminders;
